import type { EstimateAssumption, EstimateExclusion, EstimateWarning, EstimatorInput } from "../../types/index.js";
import { getScopeMultiplier } from "../scope-multipliers.js";
import {
  applyItem,
  findItem,
  scaleAppliedItem,
  type AppliedLineItem,
  type ProjectCalculationOutput,
  type ProjectCalculatorFn
} from "./shared.js";

/**
 * The Basement Estimator (Phase 8). Basement previously used the older,
 * coarser Phase 2 model (envelope categories always applied at full
 * price, optional additions gated on boolean flags with no tiers, no
 * plain-language explanation) - see docs/unresolved-decisions.md. This
 * graduates it onto the same tiered, fully-gated pattern kitchen
 * received in Phase 5A, using the question set in
 * packages/config/src/questions/basement.ts.
 *
 * Per the Phase 8 brief, this calculator distinguishes BASE basement
 * finishing from each individual scope addition - bathroom, kitchen/wet
 * bar, bedroom(s), egress, plumbing, HVAC, electrical, fireplace, and
 * ceiling upgrades are each independently priced and traceable to their
 * own `explanation.steps` entry, never folded into one flat sqft
 * multiplier.
 *
 * Current-condition handling: rather than asking the user to separately
 * size framing/drywall/insulation (categories most homeowners can't
 * usefully estimate), those three "envelope" categories are derived from
 * `basement_current_condition` - an unfinished basement needs the full
 * envelope, a partially-finished one needs less, and a previously-
 * finished basement being renovated mostly already has one (see
 * CONDITION_ENVELOPE_MULTIPLIERS below). Flooring and ceiling are still
 * asked directly (as tiers), since even a previously-finished basement
 * being renovated is usually replacing both.
 */

const CONDITION_ENVELOPE_MULTIPLIERS: Record<string, number> = {
  unfinished: 1.0,
  partially_finished: 0.6,
  previously_finished_renovating: 0.25
};

const BATHROOM_ITEM_BY_SCOPE: Record<string, string | null> = {
  none: null,
  existing_renovation: "basement_bathroom_renovation",
  new_bathroom: "basement_bathroom_addition"
};

const KITCHEN_ITEM_BY_SCOPE: Record<string, string | null> = {
  none: null,
  wet_bar: "basement_wet_bar",
  kitchenette: "basement_kitchenette",
  full_kitchen: "basement_full_kitchen"
};

const CEILING_ITEM_BY_TYPE: Record<string, string> = {
  drywall: "basement_ceiling",
  drop_ceiling: "basement_ceiling_drop",
  exposed_finished: "basement_ceiling_exposed"
};

const FLOORING_ITEM_BY_TIER: Record<string, string> = {
  basic: "basement_flooring_basic",
  mid_range: "basement_flooring",
  premium: "basement_flooring_premium"
};

const HVAC_ITEM_BY_SCOPE: Record<string, string | null> = {
  not_needed: null,
  extend_existing: "basement_hvac_extend",
  dedicated_zone: "basement_hvac"
};

function rawString(input: EstimatorInput, key: string): string | undefined {
  const value = input.rawAnswers[key];
  return typeof value === "string" ? value : undefined;
}

function selectedRooms(input: EstimatorInput): string[] {
  const value = input.rawAnswers["basement_rooms"];
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
}

export const calculateBasement: ProjectCalculatorFn = (input, catalog, regionalMultiplier) => {
  const applied: AppliedLineItem[] = [];
  const assumptions: EstimateAssumption[] = [];
  const exclusions: EstimateExclusion[] = [];
  const warnings: EstimateWarning[] = [];
  let anchorMissing = false;

  const finishLevel = input.finishLevel;
  const sqft = input.roomSize?.value;
  const condition = rawString(input, "basement_current_condition") ?? "unfinished";
  const rooms = selectedRooms(input);

  // --- Base finishing (anchor) + scope adjustment -------------------------
  let baseTypicalForPercentage = 0;
  if (sqft && sqft > 0) {
    const base = applyItem(catalog, "basement_finishing_base", { quantity: sqft, finishLevel, regionalMultiplier });
    if (base) {
      const scopeMultiplier = getScopeMultiplier(input.scope);
      const scoped = scaleAppliedItem(base, scopeMultiplier);
      applied.push(scoped);
      baseTypicalForPercentage = scoped.result.typical?.amount ?? 0;
      assumptions.push({
        code: "scope_multiplier_applied",
        message: `Base finishing cost adjusted by a ${scopeMultiplier}x scope multiplier for "${input.scope}".`
      });
    }

    // --- Envelope: framing/drywall + insulation, scaled by current condition ---
    const envelopeMultiplier = CONDITION_ENVELOPE_MULTIPLIERS[condition] ?? 1.0;
    for (const category of ["basement_framing_drywall", "basement_insulation"]) {
      const line = applyItem(catalog, category, { quantity: sqft, finishLevel, regionalMultiplier });
      if (line) applied.push(envelopeMultiplier === 1.0 ? line : scaleAppliedItem(line, envelopeMultiplier));
    }
    if (envelopeMultiplier < 1.0) {
      assumptions.push({
        code: "envelope_scope_reduced_for_condition",
        message:
          condition === "previously_finished_renovating"
            ? "Framing, drywall, and insulation costs were reduced since the space was previously finished and much of the envelope likely already exists - confirm what's being reused vs. rebuilt once walls are opened up."
            : "Framing, drywall, and insulation costs were reduced for a partially-finished basement, since some of this work already exists."
      });
    }

    // --- Flooring (discrete tier) --------------------------------------
    const flooringTier = rawString(input, "basement_flooring_type");
    const flooringItemId = flooringTier ? FLOORING_ITEM_BY_TIER[flooringTier] : "basement_flooring";
    if (flooringItemId) {
      const flooring = applyItem(catalog, flooringItemId, { quantity: sqft, finishLevel, regionalMultiplier });
      if (flooring) applied.push(flooring);
      if (!flooringTier) {
        assumptions.push({ code: "flooring_defaulted", message: 'No flooring selection was provided - assumed a mid-range flooring replacement.' });
      }
    }

    // --- Ceiling (discrete tier) -----------------------------------------
    const ceilingType = rawString(input, "basement_ceiling_type");
    const ceilingItemId = ceilingType ? CEILING_ITEM_BY_TYPE[ceilingType] : "basement_ceiling";
    if (ceilingItemId) {
      const ceiling = applyItem(catalog, ceilingItemId, { quantity: sqft, finishLevel, regionalMultiplier });
      if (ceiling) applied.push(ceiling);
      if (!ceilingType) {
        assumptions.push({ code: "ceiling_type_defaulted", message: 'No ceiling type was provided - assumed a standard drywall ceiling.' });
      }
    }
  } else {
    anchorMissing = true;
  }

  // --- Bathroom (tiered) -----------------------------------------------
  const bathroomScope = rawString(input, "basement_bathroom_scope") ?? "none";
  const bathroomItemId = BATHROOM_ITEM_BY_SCOPE[bathroomScope];
  if (bathroomItemId) {
    const bathroom = applyItem(catalog, bathroomItemId, { finishLevel, regionalMultiplier });
    if (bathroom) applied.push(bathroom);
  } else {
    exclusions.push({ code: "excludes_basement_bathroom", message: "No basement bathroom work was requested - excluded from this estimate." });
  }

  // --- Kitchen / wet bar (tiered) ---------------------------------------
  const kitchenScope = rawString(input, "basement_kitchen_scope") ?? "none";
  const kitchenItemId = KITCHEN_ITEM_BY_SCOPE[kitchenScope];
  if (kitchenItemId) {
    const kitchenWetBar = applyItem(catalog, kitchenItemId, { finishLevel, regionalMultiplier });
    if (kitchenWetBar) applied.push(kitchenWetBar);
  } else {
    exclusions.push({ code: "excludes_basement_kitchen_wet_bar", message: "No basement kitchen/wet bar work was requested - excluded from this estimate." });
  }

  // --- Bedroom(s) (from room composition) ---------------------------------
  const bedroomSelected = rooms.includes("bedroom");
  if (bedroomSelected) {
    const bedroom = applyItem(catalog, "basement_bedroom", { finishLevel, regionalMultiplier });
    if (bedroom) applied.push(bedroom);
  } else {
    exclusions.push({ code: "excludes_basement_bedroom", message: "A basement bedroom was not selected - excluded from this estimate." });
  }

  // --- Office build-out (from room composition) ---------------------------
  if (rooms.includes("office")) {
    const office = applyItem(catalog, "basement_office_buildout", { finishLevel, regionalMultiplier });
    if (office) applied.push(office);
  }

  // --- Egress (tri-state, tied to bedroom + special-handling warning) ----
  const egressStatus = rawString(input, "basement_egress_status");
  if (egressStatus === "new_or_modified_required") {
    const egress = applyItem(catalog, "basement_egress_window", { regionalMultiplier });
    if (egress) applied.push(egress);
    warnings.push({
      code: "basement_egress_requires_verification",
      message:
        "A new or modified egress window was indicated. Egress requirements depend on window-well dimensions, sill height, and local code - this estimate includes a placeholder allowance, but the actual requirement must be verified on-site before finalizing budget.",
      severity: "caution"
    });
  } else if (bedroomSelected && egressStatus !== "existing_compliant") {
    // A bedroom was selected but egress wasn't confirmed compliant (either
    // left unanswered or explicitly "not required", which is itself very
    // likely wrong for a bedroom under most Canadian codes).
    warnings.push({
      code: "basement_bedroom_egress_unconfirmed",
      message:
        "A basement bedroom was selected without confirming a compliant egress window. Most Canadian building codes require a code-compliant egress window for a legal bedroom - this was not priced or confirmed and should be verified with your municipality before finalizing budget.",
      severity: "caution"
    });
  }
  if (!bedroomSelected && egressStatus === "new_or_modified_required") {
    assumptions.push({
      code: "egress_without_bedroom",
      message: "Egress work was requested without a bedroom being selected - included as requested, but confirm this is intentional (egress is most commonly needed for a legal bedroom)."
    });
  }

  // --- Mechanical: electrical / plumbing / HVAC ---------------------------
  if (input.workType.electricalChanges === "yes") {
    const electrical = applyItem(catalog, "basement_electrical", { finishLevel, regionalMultiplier });
    if (electrical) applied.push(electrical);
  } else {
    exclusions.push({ code: "excludes_basement_electrical", message: "Excludes electrical changes - none were requested for the basement." });
  }

  if (input.workType.plumbingChanges === "yes") {
    const plumbing = applyItem(catalog, "basement_plumbing", { finishLevel, regionalMultiplier });
    if (plumbing) applied.push(plumbing);
  } else {
    exclusions.push({ code: "excludes_basement_plumbing", message: "Excludes general plumbing changes - none were requested for the basement (beyond any bathroom/kitchen addition priced above)." });
  }

  const hvacScope = rawString(input, "basement_hvac_scope");
  const hvacItemId = hvacScope ? HVAC_ITEM_BY_SCOPE[hvacScope] : null;
  if (hvacItemId) {
    const hvac = applyItem(catalog, hvacItemId, { finishLevel, regionalMultiplier });
    if (hvac) applied.push(hvac);
  } else if (!hvacScope || hvacScope === "not_needed") {
    exclusions.push({ code: "excludes_basement_hvac", message: "No basement HVAC work was requested - excluded from this estimate." });
  }

  // --- Additional features -------------------------------------------------
  if (input.rawAnswers["basement_fireplace"] === true) {
    const fireplace = applyItem(catalog, "basement_fireplace", { finishLevel, regionalMultiplier });
    if (fireplace) applied.push(fireplace);
  } else {
    exclusions.push({ code: "excludes_basement_fireplace", message: "A fireplace was not requested - excluded from this estimate." });
  }

  if (input.rawAnswers["basement_built_ins"] === true) {
    const builtIns = applyItem(catalog, "basement_built_ins", { finishLevel, regionalMultiplier });
    if (builtIns) applied.push(builtIns);
  } else {
    exclusions.push({ code: "excludes_basement_built_ins", message: "Built-in cabinetry/shelving was not requested - excluded from this estimate." });
  }

  if (input.rawAnswers["basement_soundproofing"] === true && sqft && sqft > 0) {
    const soundproofing = applyItem(catalog, "basement_soundproofing", { quantity: sqft, finishLevel, regionalMultiplier });
    if (soundproofing) applied.push(soundproofing);
  } else {
    exclusions.push({ code: "excludes_basement_soundproofing", message: "Soundproofing was not requested - excluded from this estimate." });
  }

  // --- Demolition ------------------------------------------------------------
  if (input.workType.demolitionRequired === "yes" && findItem(catalog, "basement_demolition")) {
    const demolition = applyItem(catalog, "basement_demolition", { regionalMultiplier, baseAmountForPercentage: baseTypicalForPercentage });
    if (demolition) applied.push(demolition);
  }

  // --- Accessibility --------------------------------------------------------
  if (input.accessibility.required === "yes") {
    const accessibility = applyItem(catalog, "basement_accessibility", { finishLevel, regionalMultiplier });
    if (accessibility) applied.push(accessibility);
  }

  // --- Permit / design allowance ---------------------------------------------
  if (input.designAndPermits.needsDesignServices === "yes" || input.designAndPermits.permitLikelyRequired !== "no") {
    const permitDesign = applyItem(catalog, "basement_permit_design", { finishLevel, regionalMultiplier });
    if (permitDesign) applied.push(permitDesign);
  }

  // --- Special handling: standing, basement-specific disclosures ----------
  // Per the Phase 8 brief, these must be surfaced clearly rather than left
  // implicit, since none of them can be resolved from a self-reported
  // questionnaire alone - they require an actual site visit or engineer.
  warnings.push({
    code: "basement_moisture_foundation_unknown",
    message:
      "Moisture and foundation conditions behind existing finishes cannot be assessed from a questionnaire. This estimate assumes no hidden water infiltration, mold, or foundation repair is needed - a site inspection may materially change pricing if issues are found once walls are opened.",
    severity: "caution"
  });
  if (condition === "previously_finished_renovating") {
    warnings.push({
      code: "basement_previously_finished_inspection_notice",
      message:
        "Because this basement was previously finished, actual scope (what's salvageable vs. what needs to be redone) can only be confirmed once existing finishes are opened up - a site inspection may materially change this estimate.",
      severity: "caution"
    });
  }
  if (input.workType.structuralChanges === "yes" || input.workType.structuralChanges === "unknown") {
    assumptions.push({
      code: "basement_structural_engineering_flag",
      message:
        "Structural changes were indicated (e.g. support posts/beams, load-bearing walls). This estimate does not include engineering costs - a structural engineer's assessment and likely a structural permit are required before final pricing."
    });
  }

  const output: ProjectCalculationOutput = { applied, assumptions, exclusions, anchorMissing, warnings };
  return output;
};
